/*
 * Outreach autodrive: what the admin API sends back. Mirrors the backend
 * interfaces in firebaseFunctions/functions/src/data/pg/outreach.data.ts,
 * data/pg/copilot.data.ts, services/conversation.service.ts,
 * services/simulator.service.ts, services/copilot.service.ts and
 * playbooks/{causes,sim-scripts}.ts. Dates are ISO strings.
 */

export type DriveMode = 'assisted' | 'auto' | 'paused' | 'human' | 'closed';
export const DRIVE_MODES: DriveMode[] = ['assisted', 'auto', 'paused', 'human', 'closed'];

export type ConversationState =
  | 'opener_due'
  | 'awaiting_bot'
  | 'nudge_due'
  | 'draft_pending'
  | 'sending'
  | 'awaiting_member'
  | 'done';

export const INTEL_TYPES = ['feedback', 'idea', 'lead', 'blocker', 'referral', 'praise'] as const;
export const INTEL_STATUSES = ['new', 'acknowledged', 'in_progress', 'done', 'dropped'] as const;
export const MEMBER_TAGS = ['core', 'organizer_potential', 'do_not_approach', 'advocate', 'referred_by'];

export interface OutreachConversationDto {
  id: number;
  userId: string;
  fromUid: string;
  chatId: string;
  cause: string;
  eventId: string | null;
  mode: DriveMode;
  resumeMode: 'assisted' | 'auto';
  state: ConversationState;
  simulated: boolean;
  /** A test conversation with an internal test-target account; kept out of counts. */
  isTest: boolean;
  simPersona: Record<string, unknown> | null;
  dueAt: string | null;
  botTurns: number;
  maxBotTurns: number;
  waitCount: number;
  freezeReason: string | null;
  startedBy: string;
  proposalId: number | null;
  lastMemberMessageAt: string | null;
  lastBotMessageAt: string | null;
  adminSeenAt: string | null;
  closedAt: string | null;
  closeReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One row of GET /outreach/conversations and GET /outreach/sim/sessions. */
export interface OutreachConversationRowDto extends OutreachConversationDto {
  memberName: string;
  pendingDraft: boolean;
  lastActivityAt: string | null;
}

export interface OutreachTurnDto {
  id: number;
  conversationId: number;
  messageId: string | null;
  role: 'bot' | 'member' | 'human';
  content: string;
  /** For a sent draft: { draftId, edited, ...the brain's output }. */
  brainOutput: Record<string, unknown> | null;
  sentAt: string;
}

export type DraftStatus = 'pending' | 'approved' | 'edited' | 'rejected' | 'expired' | 'sent';

export interface OutreachDraftDto {
  id: number;
  conversationId: number;
  content: string;
  brainOutput: Record<string, unknown>;
  proposedSendAt: string;
  status: DraftStatus;
  /** An admin email, or `rule:<name>` when the checks stopped it. */
  decidedBy: string | null;
  decidedAt: string | null;
  finalContent: string | null;
  editReason: string | null;
  rejectReason: string | null;
  sendAt: string | null;
  sentTurnId: number | null;
  createdAt: string;
}

export interface OutreachIntelDto {
  id: number;
  conversationId: number | null;
  userId: string | null;
  memberName: string | null;
  eventId: string | null;
  type: string;
  quote: string;
  summary: string;
  confidence: number;
  status: string;
  owner: string | null;
  dueAt: string | null;
  notes: string | null;
  simulated: boolean;
  isTest: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ModeChangeDto {
  id: number;
  conversationId: number;
  fromMode: string;
  toMode: string;
  /** An admin email, `sharon`, or `rule:<reason>`. */
  changedBy: string;
  note: string | null;
  changedAt: string;
}

export interface StyleNoteDto {
  id: number;
  note: string;
  cause: string | null;
  learnedFrom: number | null;
  acceptedBy: string;
  acceptedAt: string;
  retiredAt: string | null;
  retiredBy: string | null;
  retireReason: string | null;
}

export interface ReadinessDto {
  lastDecidedDrafts: number;
  unEditedInARow: number;
  rejectionsInLast20: number;
  freezesInLast10Conversations: number;
  ready: boolean;
}

export interface CauseDto {
  id: string;
  label: string;
  goal: string;
  playbook?: string;
  maxBotTurns?: number;
}

export interface ProposalDto {
  id: number;
  sessionId: number | null;
  action: string;
  payload: Record<string, unknown>;
  reason: string;
  status: string;
  decidedBy: string | null;
  decidedAt: string | null;
  result: unknown;
  createdAt: string;
}

export interface DecisionDto {
  id: number;
  kind: string;
  subject: string | null;
  statement: string;
  rationale: string | null;
  decidedBy: string;
  decidedAt: string;
  reversedAt: string | null;
  reversedBy: string | null;
  reversalReason: string | null;
  supersededBy: number | null;
}

export interface CopilotSessionDto {
  id: number;
  adminEmail: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CopilotItemDto =
  | { kind: 'admin' | 'copilot'; text: string; at: string }
  | { kind: 'tool'; name: string; input: unknown; at: string }
  | { kind: 'proposal'; proposalId: number; at: string };

export interface CopilotViewDto {
  session: CopilotSessionDto;
  items: CopilotItemDto[];
  proposals: ProposalDto[];
}

export interface CopilotTurnDto {
  sessionId: number;
  reply: string;
  proposals: ProposalDto[];
  view: CopilotViewDto;
}

/** GET /outreach/conversations/:id, and the tail of every call that changes one. */
export interface ConversationViewDto {
  conversation: OutreachConversationDto;
  member: { id: string; name: string; city: string | null; tier: string | null } | null;
  cause: CauseDto | null;
  causeTrusted: boolean;
  turns: OutreachTurnDto[];
  drafts: OutreachDraftDto[];
  modeChanges: ModeChangeDto[];
  intel: OutreachIntelDto[];
  readiness: ReadinessDto;
  /** accept_style_note proposals learned from this conversation's drafts, any status. */
  learned: ProposalDto[];
  persona?: Record<string, unknown> | null;
}

export interface ReplyVerdictDto {
  ok: boolean;
  text: string;
  freezeReason: string | null;
  problems: string[];
}

export interface BrainOutputDto {
  action: 'reply' | 'wait' | 'freeze' | 'close';
  reply: string | null;
  freeze_reason: string | null;
  sentiment: number;
  goal_progress: 'none' | 'partial' | 'reached';
  intel: { type: string; quote: string; summary: string; confidence: number }[];
}

export interface TurnOutcomeDto {
  conversationId: number;
  action: string;
  brain: { stub: boolean; error: string | null; model: string; output: BrainOutputDto };
  checks: ReplyVerdictDto | null;
  draft: OutreachDraftDto | null;
  sentTurnId: number | null;
  frozen: { reason: string; detail: string | null } | null;
  closed: boolean;
  rescheduledTo: string | null;
  intelRecorded: number;
  intelDropped: number;
  pacedSendAt: string | null;
  note: string | null;
}

export interface SimStepDto {
  conversationId: number;
  outcome: TurnOutcomeDto | null;
  pacing: { wouldActAt: string | null; note: string };
  fastPath: { botSuspicion: boolean; stopRequest: boolean } | null;
  brainIsStub: boolean;
}

export type SimStepViewDto = ConversationViewDto & { step: SimStepDto };

export interface SimPersonaDto {
  id: string;
  label: string;
  firstName: string;
  city?: string | null;
  eventTitle?: string | null;
  eventEndedDaysAgo?: number | null;
  lobbyActivity?: string;
  peerChats?: string;
  recency?: string;
  tier?: string | null;
  circle?: string[];
}

export interface SimScriptDto {
  id: string;
  label: string;
  expect: string;
  messages: string[];
}

export interface SimScriptsDto {
  personas: SimPersonaDto[];
  scripts: SimScriptDto[];
  causes: CauseDto[];
  brainIsStub: boolean;
  fastClock: number;
}

export interface OutreachOverviewDto {
  counts: {
    open: number;
    frozen: number;
    pending_drafts: number;
    new_intel: number;
    overdue_intel: number;
    pending_proposals: number;
  };
  causes: (CauseDto & { enabled: boolean; trusted: boolean; readiness: ReadinessDto })[];
  claudeConfigured: boolean;
  drainerEnabled: boolean;
  fastClock: number;
  simulatorAvailable: boolean;
  tags: string[];
}

export interface DecideDraftBody {
  kind: 'approve' | 'edit' | 'reject';
  text?: string;
  when?: 'now' | 'proposed';
  reason?: string;
  redraft?: boolean;
}

export type DecideDraftResultDto = ConversationViewDto & {
  draft: OutreachDraftDto;
  sent: boolean;
  learn: boolean;
};

export interface LearnResultDto {
  proposal: ProposalDto | null;
  info: Record<string, unknown>;
}

export interface MemberTagDto {
  tag: string;
  setBy: string;
  setAt: string;
  note: string | null;
}

export interface MemberOutreachDto {
  open: OutreachConversationDto | null;
  latest: OutreachConversationDto | null;
  tags: MemberTagDto[];
  lastContactedAt: string | null;
  causes: { id: string; label: string; enabled: boolean; trusted: boolean }[];
}

export interface IntelPatch {
  status?: string;
  owner?: string | null;
  dueAt?: string | null;
  notes?: string | null;
}

/** Error bodies: `{ error, ...details }` with a 4xx/5xx status. */
export interface OutreachErrorBody {
  error: string;
  problems?: string[];
  needsForce?: boolean;
  conversationId?: number;
}

/** The message to show for a failed call, preferring the server's own words. */
export function outreachError(err: any, fallback = 'Something went wrong'): string {
  const body = err?.error;
  if (body && typeof body === 'object' && typeof body.error === 'string') return body.error;
  if (typeof body === 'string' && body.trim() && body.length < 300) return body;
  return `${fallback}: ${err?.message ?? err}`;
}

export const MODE_LABELS: Record<DriveMode, string> = {
  assisted: 'Assisted',
  auto: 'Auto',
  paused: 'Paused',
  human: 'Taken over',
  closed: 'Closed',
};

export const STATE_LABELS: Record<ConversationState, string> = {
  opener_due: 'Opener due',
  awaiting_bot: 'Sharon to reply',
  nudge_due: 'Follow-up due',
  draft_pending: 'Draft waiting',
  sending: 'Sending',
  awaiting_member: 'Waiting for member',
  done: 'Done',
};

/** Freeze reasons as a person would say them. */
export const FREEZE_LABELS: Record<string, string> = {
  bot_suspicion: 'Asked if Sharon is a bot',
  complaint: 'Complaint',
  privacy: 'Privacy question',
  account_or_pricing: 'Account or pricing question',
  stop_request: 'Asked to stop',
  legal: 'Legal',
  cannot_answer: 'Sharon cannot answer',
  unsure: 'Sharon was unsure',
  paused_by_admin: 'Paused by the team',
};

export const PROPOSAL_LABELS: Record<string, string> = {
  start_conversation: 'Start a conversation',
  set_conversation_mode: 'Change a conversation mode',
  close_conversation: 'Close a conversation',
  tag_member: 'Tag a member',
  untag_member: 'Remove a member tag',
  record_decision: 'Record a decision',
  reverse_decision: 'Reverse a decision',
  set_intel_status: 'Update an intel item',
  enable_cause: 'Enable a cause',
  mark_cause_trusted: 'Trust a cause with auto',
  accept_style_note: 'Add a style note',
  retire_style_note: 'Retire a style note',
};
